import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";
import { connect } from "react-redux";
import Link from "@insite/mobius/Link";
import { getLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

const mapStateToProps = (state: ApplicationState) => ({
    state
});
interface IndustryItem {
    image: string;
    label: string;
    backgroundColor: string;
    textColor: string;
    buttonLink: LinkFieldValue;
}

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;
interface PropsComponent extends Props {
    fields: {
        esgPageGrid: IndustryItem[];
        isFullWidth: boolean;
    };
}

const styles: {
    iconGrid: InjectableCss;
    iconItem: GridItemProps;
    iconImage: InjectableCss;
    iconLabel: InjectableCss;
    iconColor?: InjectableCss;
    buttonLink?: InjectableCss;
    buttonLink2?: InjectableCss;
    imageWrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    topRightCornerIcon?: InjectableCss;
} = {
    iconGrid: {
        css: css`
            gap: 20px !important;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            >div{
                justify-content: center;
            }
             
         ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        padding:0px !important;
                    `,
                    css`
                        padding:0px !important;
                    `,
                    css`
                        padding:0px !important;
                    `,
                    css`
                        padding:0px 30px !important;
                        
                    `,
                    css`
                        padding:0px 70px !important;
                    ` 
                ],
            )}
            
        

        `,
    },
    iconItem: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            flex-grow: 1 !important;
        `,
    },
    iconImage: {
        css: css`
            width: 150px;
            height: 150px;
        `,
    },
    iconLabel: {
        css: css`
            font-size: 1.5rem;
            line-height: normal;
            text-transform: uppercase;
            font-weight: 600;
            font-family: Barlow-Regular;
        `,
    },
    iconColor:{
        css:css`
        font-size: 0.9rem;
        text-align: center;
        font-family: SourceSans3-Regular;
        `
    },
    buttonLink:{
        css:css`
        cursor: pointer;
        width: 100%;
        >span{
        width: 100%;
        }
        `
    },
    buttonLink2:{
        css:css`
        cursor: default;
        width: 100%;
        >span{
        width: 100%;
        }
        `
    },
    imageWrapper:{
        css:css`
            display: flex;
            flex-direction: row;
            align-items: flex-end;
            justify-content: space-between;
            height: 150px;
        
        `
    },
    textWrapper:{
        css:css`
            flex-grow: 1;
            height: 100%;
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            padding: 15px;
            flex-direction: column;
            text-align: right;
        `
    },
    topRightCornerIcon:{
        css:css`
            border-left: 15px solid transparent;
            border-bottom: 15px solid transparent;
        `
    }
};


export const EsgSectionGrid: React.FC<PropsComponent> = ({ fields, state }) => {
   
    let esgPageGrid = (fields?.esgPageGrid || []).map(item =>
        {
            let links;
            if((item as any).fields?.buttonLink){
                links = getLink(state, (item as any).fields?.buttonLink);
            }
            return (item as any).fields ? {...(item as any).fields,links} : {...item,links}
        }
    );
    return (
        <GridContainer {...(fields.isFullWidth ? {} : styles.iconGrid)}>
            {esgPageGrid?.map((esgPage, idx) =>{
                return (
                <GridItem width={fields.isFullWidth ? [12,12,12,12,12]:[12,12,6,5,5]} {...styles.iconItem} key={idx}>
                    <Link
                        href={esgPage?.links?.url || ""}
                        {...(esgPage?.links?.url ? { ...styles.buttonLink } : { ...styles.buttonLink2 })}
                    >
                        <StyledWrapper
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "flex-end",
                                justifyContent: "space-between",
                                height: "150px",
                                background: esgPage?.backgroundColor ? esgPage?.backgroundColor:"#ffffff"
                            }}
                        >
                            <Img
                                src={esgPage.image}
                                alt={esgPage.label}
                                {...styles.iconImage}
                            />
                            <StyledWrapper {...styles.textWrapper}>
                                <StyledWrapper 
                                    style={{
                                        borderLeft: "15px solid transparent",
                                        borderBottom: "15px solid transparent",
                                        borderTop: `15px solid ${esgPage?.textColor}`,
                                        borderRight: `15px solid ${esgPage?.textColor}`
                                    }}
                                ></StyledWrapper>
                                <Typography 
                                style={{
                                    fontSize: "1.5rem",
                                    lineHeight: "normal",
                                    textTransform: "uppercase",
                                    fontWeight: 600,
                                    fontFamily: "Barlow-Regular",
                                    color: `${esgPage?.textColor}`
                                }}
                                >{esgPage.label}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </Link>
                </GridItem>
            )})}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component:  connect(mapStateToProps)(EsgSectionGrid),
    definition: {
        group: "Basic",
        icon: "image",
        displayName: "ESG Section Grid",
        fieldDefinitions: [
            {
                name: "isFullWidth",
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: "esgPageGrid",
                displayName: "ESG Page Grid",
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: ({ fields: { label } }) => label || "Section",
                fieldDefinitions: [ 
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "label",
                        displayName: "Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "backgroundColor",
                        displayName: "Background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },{
                        name: "textColor",
                        displayName: "Text Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
                defaultValue: [],
            },
],
    },
};

export default widgetModule;